import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import ReactSelect from 'react-select';
import { List } from 'react-virtualized';

import {
	closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors
} from '@dnd-kit/core';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import {
	rectSortingStrategy, SortableContext, sortableKeyboardCoordinates
} from '@dnd-kit/sortable';

import {
	GetNewOption, GetNewQuestion, InsertSurvey, UpdateSurvey
} from '../../../../../../connection/surveys';
import { useTranslate } from '../../../../../../i18n/useTranslationModules';
import { updateHomepageModule } from '../../../../../../store/actions/admin/create-event';
import { updatePageModule } from '../../../../../../store/actions/admin/create-event/session';
import {
	addSurvey as dispatchAddSurvey, getSurveys, updateSurvey
} from '../../../../../../store/actions/admin/surveys';
import { addChecklistItem } from '../../../../../../store/actions/authentication';
import { ChecklistTitles } from '../../../../../../store/reducers/admin/assistant';
import { useTypedSelector } from '../../../../../../store/reducers/use-typed-selector';
import { GetDefaultSurvey } from '../../../../../../store/utils/create-event';
import { TemplateClassNames } from '../../../../../../types/template-layouts';
import {
	ColorKeys, CreateSurvey, FeatureFlagsEnum, LanguagesAbbr, PageModule, Survey, SurveyQuestion,
	SurveyQuestionOption,
	SurveyQuestionType, SurveyType, TextOverrides
} from '../../../../../../types/working-model';
import { getTextValue, updateTranslateKey } from '../../../../../../utils/utils';
import { showAlert } from '../../../../../general-ui/alert/alert-service';
import LargeButton from '../../../../../general-ui/button/large-button';
import Divider from '../../../../../general-ui/divider/divider';
import {
	EContentColorOverrideNames
} from '../../../../../general-ui/edit-section-colors/edit-section-colors-modal';
import EditableTextSlim from '../../../../../general-ui/editable-text/editable-text-slim';
import Icon, { COLORS, ICONS } from '../../../../../general-ui/icon';
import ModalComponent from '../../../../../general-ui/modal/modal';
import { reactSelectStyles } from '../../../../../general-ui/react-select/react-select-styles';
import Switch from '../../../../../general-ui/switch/switch';
import TextInput from '../../../../../general-ui/text-input/text';
import { Tooltip } from '../../../../../general-ui/tooltip/tooltip';
import WaitingIndicator from '../../../../../general-ui/waiting-indicator/waiting-indicator';
import useTranslationRoute from '../../../../../live-event/hooks/use-translation-route';
import { ParamsProps } from '../../../../../live-event/live-event';
import { getStylingOverrides } from '../../../../../live-event/utils';
import Controls from '../../session-module-controls/controls';
import EventLevelControls, { InitialControlsDisplay } from '../../../session/editor/moduleControls/controls';

import EditorSurvey from './survey';
import { SurveyQuestionComponent } from './survey-question';
import { SurveyQuizForm } from './survey-quiz-form';
import SurveySelectItem from './survey-select-item';

import '../../../../../../scss/live-event/base/session/survey.scss';
import './surveys.scss';

interface Props {
	isLandingPage?: boolean;
	page_module: PageModule;
	template: string;
}

enum SurveySelectionSteps {
	select,
	create,
	edit,
	questionsList,
	question,
	quizModal
}

const Surveys: React.FC<Props> = React.forwardRef<HTMLDivElement, Props>((props: Props, ref): JSX.Element | null => {
	const { page_module, template, isLandingPage } = props;
	const isEventLevel = isLandingPage ?? false;

	const workingSession = useTypedSelector(state => state.CreateSessionReducer.workingSession);
	const token = useTypedSelector(state => state.AuthReducer.token);
	const user = useTypedSelector(state => state.AuthReducer.user);
	const isLoadingSurveys = useTypedSelector(state => state.SurveysReducer.isLoadingSurveys);
	const surveys = useTypedSelector(state => state.SurveysReducer.surveys);
	const featureFlags = useTypedSelector(state => state.FeatureFlagsReducer.featureFlags);
	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const dispatch = useDispatch();

	// Languages
	const { language } = useParams() as ParamsProps;
	const namespace = useTranslationRoute();
	const { t } = useTranslate(namespace);
	const baseLanguage = (workingSession ? workingSession?.default_language : workingEvent?.settings.i18n?.base) as LanguagesAbbr;

	// Modal and Styling
	const [modalOpen, setModalOpen] = useState(false);
	const content = { ...GetDefaultSurvey(), ...page_module?.content };
	const { custom_heading, title } = getTextValue(page_module?.content, language);
	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);
	const styling_overrides = page_module?.styling_overrides;
	const { backgroundColor, backgroundImg } = getStylingOverrides(styling_overrides);
	const defaultCustomHeadingColor = template === TemplateClassNames.O2
		? ColorKeys.bodyTextColor
		: ColorKeys.secondaryAccentColor;

	// Forms and Fields
	const [activeStep, setActiveStep] = useState<SurveySelectionSteps>(SurveySelectionSteps.select);
	const [saving, setSaving] = useState(false);
	const [selectedSurveyIds, setSelectedSurveyIds] = useState<number[] | null>(page_module.content_modules ?? null);
	const [workingQuestion, setWorkingQuestion] = useState<SurveyQuestion | null>(null);
	const [workingSurvey, setWorkingSurvey] = useState<Survey | CreateSurvey | null>(null);
	const isQuiz = workingSurvey?.survey_type === SurveyType.quiz;

	// Validation
	const [questionErrors, setQuestionErrors] = useState<(string)[]>([]);
	const [surveyErrors, setSurveyErrors] = useState<(string)[]>([]);

	// Search and Lists
	const [searchTerm, setSearchTerm] = useState('');
	const visibleSurveys = useMemo(() => {
		if (!searchTerm) return surveys;

		return surveys.filter((survey: Survey) =>
			survey.name.base
				.toLowerCase()
				.trim()
				.replace(/ /g, '')
				.includes(searchTerm)
		);
	}, [searchTerm, surveys]);

	const closeModal = useCallback(() => {
		setSearchTerm("");
		setModalOpen(false);
	}, []);

	// Gets surveys on mount and following page_module update
	useEffect(() => {
		if (token) dispatch(getSurveys(token));
	}, [dispatch, page_module.content_modules, token]);

	// Resets the forms on modal open/close
	useEffect(() => {
		const selectedIds = modalOpen ? (page_module.content_modules ?? []) as number[] : null;

		setSelectedSurveyIds(selectedIds);
		setActiveStep(SurveySelectionSteps.select);
		setWorkingSurvey(null);
		setWorkingQuestion(null);
		setQuestionErrors([]);
		setSurveyErrors([]);
	}, [modalOpen, page_module.content_modules]);

	// Functions with no deps can be memoized together here
	const Memoized: { [key: string]: any } = useMemo(() => ({
		deleteQuizQuestion(question: SurveyQuestion) {
			setWorkingSurvey((curWorkingSurvey) => {
				if (!curWorkingSurvey) return curWorkingSurvey;
				const updatedQuestions = curWorkingSurvey.questions.filter((q) => q.survey_question !== question.survey_question);
				const updatedSurvey = {
					...curWorkingSurvey,
					questions: updatedQuestions,
				};

				return updatedSurvey;
			});
			setWorkingQuestion(null);
		},
		editQuestion(question: SurveyQuestion) {
			setWorkingQuestion(question);
			setActiveStep(SurveySelectionSteps.question);
		},
		editQuizQuestion(question: SurveyQuestion) {
			setWorkingQuestion(question);
			setActiveStep(SurveySelectionSteps.quizModal);
		},
		editSurvey(survey: Survey) {
			setWorkingSurvey(survey);
			setActiveStep(SurveySelectionSteps.edit);
		},
		handleDragEnd(event: any) {
			setWorkingSurvey((curSurvey) => {
				if (!curSurvey) return null;

				const questions = [...curSurvey.questions];
				const startIndex = questions.findIndex(
					(question: SurveyQuestion) =>
						Number(question.survey_question) === Number(event.active.id)
				);
				const newIndex = questions.findIndex(
					(question: SurveyQuestion) =>
						Number(question.survey_question) === Number(event.over.id)
				);

				const [question] = questions.splice(startIndex, 1);
				questions.splice(newIndex, 0, question);

				return { ...curSurvey, questions };
			});
		},
		handleDragEndOptions(event: any) {
			setWorkingQuestion((curQuestion) => {
				if (!curQuestion) return null;
				const options = [...curQuestion.options];
				const startIndex = options.findIndex(
					(option: SurveyQuestionOption) =>
						Number(option.survey_question_option) === Number(event.active.id)
				);
				const newIndex = options.findIndex(
					(option: SurveyQuestionOption) =>
						Number(option.survey_question_option) === Number(event.over.id)
				);

				const [option] = options.splice(startIndex, 1);
				options.splice(newIndex, 0, option);
				return { ...curQuestion, options };
			});
		},
		handleOpenSurveyModal() {
			setModalOpen(true);
		},
		handleSearch({ target: { value } }: React.ChangeEvent<HTMLInputElement>) {
			setSearchTerm(
				value
					.toLowerCase()
					.trim()
					.replace(/ /g, '')
			);
		},
		selectSurvey(shouldRemove: boolean, survey: Survey) {
			setSelectedSurveyIds((curSelected) => {
				if (!curSelected) return curSelected;

				const updated = shouldRemove
					? curSelected.filter((id) => id !== survey.survey)
					: [...curSelected, survey.survey];

				return updated;
			});
		},
		setOptionLabel(index: number) {
			return ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
				setWorkingQuestion((question) => {
					if (!question) { return null; }
					return {
						...question,
						options: question.options.map((option, _index) => {
							if (_index === index) {
								option.label = updateTranslateKey({
									translateString: option.label,
									input: value,
									language,
									baseLanguage
								});
							}
							return option;
						}),
					};
				});
			};
		},
		setQuestionType(value: SurveyQuestionType) {
			setWorkingQuestion((question) => {
				if (!question) return null;
				return {
					...question,
					question_type: value,
				};
			});
		},
		toggleAllowRetake(_value: any, isOn: boolean) {
			setWorkingSurvey((curWorkingSurvey) => {
				if (!curWorkingSurvey) { return null; }

				return {
					...curWorkingSurvey,
					allow_retake: isOn,
				};
			});
		},
		toggleShowResults(_value: any, isOn: boolean) {
			setWorkingSurvey((curWorkingSurvey) => {
				if (!curWorkingSurvey) return null;

				// Quizzes cannot show results
				const showResults = isOn && curWorkingSurvey?.survey_type !== SurveyType.quiz;

				return {
					...curWorkingSurvey,
					show_results: showResults,
				};
			});
		},
		toggleSurveyType() {
			setWorkingSurvey((workingSurvey) => {
				if (!workingSurvey) return null;

				// Quizzes cannot currently show results
				const surveyType = workingSurvey.survey_type === SurveyType.quiz ? SurveyType.survey : SurveyType.quiz; // can't convert from quiz type to any other type
				const showResults = workingSurvey.show_results && surveyType !== SurveyType.quiz; // if quiz, disable show results

				return {
					...workingSurvey,
					survey_type: surveyType,
					show_results: showResults,
				};
			});
		},
		validateQuizQuestion(question: SurveyQuestion | null) {
			const errors: string[] = [];

			if (!question) {
				errors.push('Please enter a valid question.');
				setQuestionErrors(errors);
				return errors;
			}

			const { question_type, title } = question;

			// Question
			if (!title.base.trim()) {
				errors.push('Question text is required.');
			}
			if (question_type !== SurveyQuestionType.checkbox && question_type !== SurveyQuestionType.radio) {
				errors.push('Quiz questions must be single or multi-select.');
			}

			// Options
			if (!question.options.length) {
				errors.push('Please add at least one answer option.');
			}
			if (question.options.every((option) => !option.is_correct)) {
				errors.push('Please select a correct answer.');
			}
			// This should not be possible, but just in case somebody cheats in dom...
			if (question.options.filter((option) => option.is_correct).length > 1) {
				errors.push('Only one answer can be selected as correct.');
			}

			setQuestionErrors(errors);
			return errors;
		},
		validateSurvey(survey: CreateSurvey) {
			const { passing_threshold, questions, show_results, survey_type } = survey;
			const errors: string[] = [];
			const questionCount = questions?.length ?? 0;

			if (!survey_type) {
				errors.push('Please provide a survey type.');
			}
			if (!questionCount) {
				errors.push('Please add at least one question.');
			}

			// Quiz validation
			if (questionCount && survey_type === SurveyType.quiz) {
				const questionsAreValid = !!questions?.reduce((areValidSoFar, question) => {
					const questionErrors = this.validateQuizQuestion(question);
					return !questionErrors.length && areValidSoFar;
				}, true);
				if (!questionsAreValid) {
					errors.push('Please ensure all questions are valid.');
				}
				if (typeof passing_threshold !== 'number' || passing_threshold < 0 || passing_threshold > questionCount) {
					errors.push(`A quiz score needed to pass is required and can be 0 up to ${questionCount}.`);
				}
				if (show_results) {
					errors.push('Quizzes cannot show results to users.');
				}
			}

			setSurveyErrors(errors);
			return errors;
		},
	}), [baseLanguage, language]);

	const AsyncMemoized = useMemo(() => ({
		async addOption() {
			if (!token) return;
			const newOption = await GetNewOption(token);
			setWorkingQuestion((question) => {
				if (!question) { return null; }
				return {
					...question,
					options: [...question.options, newOption],
				};
			});
		},
		async addDefaultQuizOption() {
			if (!token) return;
			const newOption = await GetNewOption(token);
			return newOption;
		},
		async addQuestion() {
			if (!token) return;
			const newQuestion = await GetNewQuestion(token);
			setWorkingSurvey((workingSurvey) => {
				if (!workingSurvey) { return null; }

				// This opens the new question and keeps it off of the working quiz
				// These blank questions (and options) are disposable and we don't want them on the working quiz
				if (workingSurvey.survey_type === SurveyType.quiz) {
					newQuestion.question_type = SurveyQuestionType.radio; // quiz defaults to select one only
					setWorkingQuestion(newQuestion);
					return workingSurvey;
				}

				return {
					...workingSurvey,
					questions: [...workingSurvey.questions, newQuestion],
				};
			});
		}
	}), [token]);

	// This is a row renderer called by the surveys list component (where you select a survey)
	const RenderSurveyListItem = useCallback(({ index, style, key }: { index: number, style: React.CSSProperties, key: string }) => {
		const survey = visibleSurveys[index];

		// The whole list is conditionally rendered such that this can't happen (Typescript doesn't know)
		if (selectedSurveyIds === null) return null;

		const isSelected = selectedSurveyIds.some((id) => id === survey.survey);

		return (
			<SurveySelectItem
				isSelected={isSelected}
				key={key}
				onChange={Memoized.selectSurvey}
				onEditClick={Memoized.editSurvey}
				onDeleteClick={Memoized.deleteSurvey}
				style={style}
				survey={survey}
			/>
		);
	},
		[Memoized, selectedSurveyIds, visibleSurveys]
	);

	const doneSelectingSurveys = useCallback(() => {
		// Tiny edge case prevents update
		if (selectedSurveyIds === null) return;

		const foundSurveys: Survey[] = [];
		for (const surveyId of selectedSurveyIds) {
			const found = surveys.find((survey) => surveyId === survey.survey);
			if (found) {
				foundSurveys.push(found);
			}
		}

		if (isEventLevel) {
			dispatch(
				updateHomepageModule({
					...page_module,
					modules: foundSurveys,
					content_modules: selectedSurveyIds,
				})
			);
		} else {
			dispatch(
				updatePageModule({
					...page_module,
					modules: foundSurveys,
					content_modules: selectedSurveyIds,
				})
			);
		}

		// ASSISTANT CHECKLIST ITEM: complete users checklist item for updating a section
		if (user && token && !user.onboarded.includes(ChecklistTitles.editSection)) {
			dispatch(addChecklistItem(token, user.id, user.onboarded, ChecklistTitles.editSection));
		}

		closeModal();
	}, [closeModal, dispatch, isEventLevel, page_module, selectedSurveyIds, surveys, token, user]);

	const createNewSurvey = useCallback(() => {
		if (language === baseLanguage) {
			setWorkingSurvey(GetDefaultSurvey());
			setActiveStep(SurveySelectionSteps.create);
		} else {
			showAlert({
				message: 'Unable to Create',
				description:
					'You are only able to create new content from the default selected language.',
				type: 'error',
				duration: 5000,
			});
		}
	}, [baseLanguage, language]);

	const setWorkingSurveyTransKey = useCallback((
		key: 'name' | 'description' | 'completion_message',
		value: string
	) => {
		setWorkingSurvey((workingSurvey) => {
			if (!workingSurvey) { return null; }

			return {
				...workingSurvey,
				[key]: updateTranslateKey({
					translateString: workingSurvey[key],
					input: value,
					baseLanguage,
					language
				}),
			};
		});
	}, [language, baseLanguage]);

	const setWorkingSurveyTitle = useCallback(({
		target: { value },
	}: React.ChangeEvent<HTMLInputElement>) => {
		setWorkingSurveyTransKey('name', value);
	}, [setWorkingSurveyTransKey]);

	const setWorkingSurveyDescription = useCallback(({
		target: { value },
	}: React.ChangeEvent<HTMLInputElement>) => {
		setWorkingSurveyTransKey('description', value);
	}, [setWorkingSurveyTransKey]);

	const handleDeleteQuestion = useCallback((question: SurveyQuestion) => async () => {
		if (!workingSurvey?.questions?.length) return;
		if ('survey' in workingSurvey) {
			// remove question from survey that has already been saved
			const updatedWorkingSurvey: Survey = {
				...workingSurvey,
				questions: workingSurvey.questions.filter(qn => qn.survey_question !== question.survey_question),
			};
			try {
				if (!token) return;
				const updatedSurvey = await UpdateSurvey(token, updatedWorkingSurvey);
				dispatch(updateSurvey(updatedSurvey));
				setWorkingSurvey(updatedSurvey);
			} catch (e: any) {
				console.error(e?.response || e);
			}
		} else {
			// removing question from new survey that has not been saved yet
			setWorkingSurvey({
				...workingSurvey,
				questions: workingSurvey.questions.filter(qn => qn.survey_question !== question.survey_question),
			});
		}
	}, [workingSurvey, dispatch, token]);

	const goToQuestions = useCallback(() => {
		setWorkingQuestion(null);

		if (isQuiz) {
			setActiveStep(SurveySelectionSteps.quizModal);

			if (!workingSurvey.questions.length) {
				AsyncMemoized.addQuestion();
			}
		} else {
			setActiveStep(SurveySelectionSteps.questionsList);
		}
	}, [AsyncMemoized, isQuiz, workingSurvey]);

	const setWorkingQuestionTitle = useCallback(({
		target: { value },
	}: React.ChangeEvent<HTMLInputElement>) => {
		setWorkingQuestion((question) => {
			if (!question) { return null; }

			return {
				...question,
				title: updateTranslateKey({
					translateString: question.title,
					input: value,
					baseLanguage,
					language
				}),
			};
		});
	}, [language, baseLanguage]);

	const modalTitle = useMemo(() => {
		const isEditing = !!(workingSurvey as Survey)?.survey;
		switch (activeStep) {
			case SurveySelectionSteps.select:
				return 'Add Survey';
			case SurveySelectionSteps.edit:
				return isQuiz ? 'Edit Quiz' : 'Edit Survey';
			case SurveySelectionSteps.create:
				return isQuiz ? 'Create Quiz' : 'Create Survey';
			case SurveySelectionSteps.questionsList:
				return 'Survey Questions';
			case SurveySelectionSteps.question:
				return 'Edit Question';
			case SurveySelectionSteps.quizModal:
				return isEditing ? 'Edit Quiz' : 'Quiz Questions';
			default:
				return 'Select Surveys';
		}
		// typecast workingSurvey as Survey requires lint disable
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeStep, isQuiz, (workingSurvey as Survey)?.survey, workingSurvey?.survey_type]);

	const modalHeader = useMemo(() => {
		switch (activeStep) {
			case SurveySelectionSteps.select:
				return 'Select a survey to add to the page, or create a new one.';
			default:
				return undefined;
		}
	}, [activeStep]);

	const ModalBody = useMemo(() => {
		switch (activeStep) {
			case SurveySelectionSteps.select: {
				return (
					<>
						<div className="surveys-search">
							<TextInput
								onChange={Memoized.handleSearch}
								placeholder={'Search Surveys...'}
								icon={ICONS.SEARCH}
								size={'small'}
							/>
							<div className="surveys-search-separator"> or </div>
							<div style={{ position: 'relative' }}>
								{language !== baseLanguage ? (
									<Tooltip position='left' tooltip="Surveys may only be created in the base language">
										<button className="small" disabled>Create Survey</button>
									</Tooltip>
								) : (
									<button className="small" onClick={createNewSurvey}>Create Survey</button>
								)}
							</div>
						</div>
						{selectedSurveyIds === null || (isLoadingSurveys && !surveys.length) ? (
							<div style={{ height: '20vh' }}>
								<WaitingIndicator fillSpace />
							</div>
						) : (
							<List
								width={560}
								height={560 - 112}
								rowCount={visibleSurveys.length}
								rowHeight={96 + 16}
								rowRenderer={RenderSurveyListItem}
								overscanRowCount={12}
							/>
						)}
					</>
				);
			}
			case SurveySelectionSteps.edit: {
				return (
					<>
						<TextInput
							label={'Survey Title'}
							onChange={setWorkingSurveyTitle}
							defaultValue={workingSurvey?.name?.[language] as string ?? workingSurvey?.name.base}
							placeholder={'Enter a name for the survey'}
							id="survey-title"
						/>
						<TextInput
							label={'Survey Description'}
							onChange={setWorkingSurveyDescription}
							defaultValue={workingSurvey?.description?.[language] as string ?? workingSurvey?.description.base}
							placeholder={'Please enter a description'}
							id='survey-description'
						/>
						<Divider />
						<div className="allow-retake">
							<div className="allow-retake-context">
								<label>Allow viewers to retake a survey</label>
								<span>
									Allow all attendees to retake a survey at
									any time.
								</span>
							</div>
							<Switch
								on={!!workingSurvey?.allow_retake}
								onClick={Memoized.toggleAllowRetake}
								value={'allow-retake'}
							/>
						</div>

						<div className="allow-retake">
							<div className="allow-retake-context">
								<label>Show results to the users</label>
								<span>
									Show results to all attendees after
									completing the survey.
								</span>
							</div>
							<Switch
								disabled={isQuiz}
								on={!!workingSurvey?.show_results}
								onClick={Memoized.toggleShowResults}
								value={'show-results'}
							/>
						</div>
						{!!featureFlags[FeatureFlagsEnum.quizzes] && (
							<>
								<div className="allow-retake">
									<div className="allow-retake-context disabled">
										<label>Enable Quiz</label>
									</div>
									<Switch
										disabled
										on={isQuiz}
										onClick={() => { /* do nothing */ }}
										value={'enable-quiz'}
									/>
								</div>
							</>
						)}
					</>
				);
			}
			case SurveySelectionSteps.create: {
				return (
					<>
						<TextInput
							label={'Survey Title'}
							onChange={setWorkingSurveyTitle}
							placeholder={'Enter a name for the survey'}
							id="survey-title"
						/>
						<TextInput
							label={'Survey Description'}
							onChange={setWorkingSurveyDescription}
							placeholder={'Please enter a description'}
							id='survey-description'
						/>
						<Divider />
						<div className="allow-retake">
							<div className="allow-retake-context">
								<label>Allow viewers to retake a survey</label>
								<span>
									Allow all attendees to retake a survey at
									any time.
								</span>
							</div>
							<Switch
								on={!!workingSurvey?.allow_retake}
								onClick={Memoized.toggleAllowRetake}
								value={'allow-retake'}
							/>
						</div>
						<div className="allow-retake">
							<div className="allow-retake-context">
								<label>Show results to the users</label>
								<span>
									Show results to all attendees after
									completing the survey.
								</span>
							</div>
							<Switch
								disabled={isQuiz}
								on={!!workingSurvey?.show_results}
								onClick={Memoized.toggleShowResults}
								value={'show-results'}
							/>
						</div>
						{!!featureFlags[FeatureFlagsEnum.quizzes] && (
							<>
								<div className="allow-retake">
									<div className="allow-retake-context">
										<label>Enable Quiz</label>
									</div>
									<Switch
										on={isQuiz}
										onClick={Memoized.toggleSurveyType}
										value={'enable-quiz'}
									/>
								</div>
							</>
						)}
					</>
				);
			}
			case SurveySelectionSteps.questionsList: {
				return (
					<div className="questions-list">
						<p className="subtitle">
							Surveys are used to test viewers on their knowledge
						</p>
						{!!workingSurvey && workingSurvey.questions.length > 0 ? (
							<DndContext
								sensors={sensors}
								collisionDetection={closestCenter}
								onDragEnd={Memoized.handleDragEnd}
								modifiers={[restrictToParentElement]}
								autoScroll={false}
							>
								<SortableContext
									items={(workingSurvey
										.questions as any[]).map(
											(question: SurveyQuestion) =>
												String(question.survey_question)
										)}
									strategy={rectSortingStrategy}
								>
									<div className="questions-list">
										{(workingSurvey.questions as any[]).map(
											(question: SurveyQuestion, index: number) => (
												<SurveyQuestionComponent
													key={`survey-question-${index}`}
													id={String(question.survey_question)}
													question={question}
													Handlers={Memoized}
													handleDeleteQuestion={handleDeleteQuestion}
													language={language}
												/>
											)
										)}
									</div>
								</SortableContext>
							</DndContext>
						) : (
							<p className="survey-questions-empty">
								No questions have been added.
							</p>
						)}

						<button className="small" onClick={AsyncMemoized.addQuestion}>
							<Icon name={ICONS.ADD} size={12} color={COLORS.WHITE} /> Add Question
						</button>
					</div>
				);

			}
			case SurveySelectionSteps.question: {
				const options = [
					{
						label: 'Select One',
						value: String(SurveyQuestionType.radio),
					},
					{
						label: 'Select Multiple',
						value: String(SurveyQuestionType.checkbox),
					},
					{
						label: 'Text Question',
						value: String(SurveyQuestionType.text),
					}
				];

				return (
					<div className="edit-question">
						<div className="question-row">
							<TextInput
								value={workingQuestion?.title?.[language] as string ?? workingQuestion?.title.base}
								onChange={setWorkingQuestionTitle}
								placeholder={'Enter question'}
								label={'Your Question'}
							/>
							<ReactSelect
								tabSelectsValue={false}
								styles={reactSelectStyles({
									control: {
										borderRadius: '100px',
									}
								})}
								options={options}
								onChange={value => Memoized.setQuestionType(Number(value?.value))}
								defaultValue={
									workingQuestion?.question_type
										? options.find(option => String(option.value) === String(workingQuestion?.question_type))
										: {
											label: 'Select One',
											value: String(SurveyQuestionType.radio),
										}
								}
								menuPortalTarget={document.body}
							/>
						</div>

						{workingQuestion &&
							workingQuestion.question_type !==
							SurveyQuestionType.text && (
								<>
									{workingQuestion.options.length < 1 ? (
										<p className="survey-questions-empty">
											Please add at least one option.
										</p>
									) : (
										<>
											{workingQuestion.options.map(
												(option, index) => (
													<div
														className="survey-question-option"
														key={`survey-option-${index}`}
													>
														<TextInput
															placeholder="Enter option text"
															defaultValue={
																option.label?.[language] as string ?? option.label.base
															}
															onChange={Memoized.setOptionLabel(index)}
															label={`Option ${index + 1}`}
															id={option.label.base}
														/>
													</div>
												)
											)}
										</>
									)}
									<button
										className="small"
										onClick={AsyncMemoized.addOption}
									>
										Add Option
									</button>
								</>
							)}
					</div>
				);
			}
			case SurveySelectionSteps.quizModal: {
				return (
					<SurveyQuizForm
						addDefaultQuizOption={AsyncMemoized.addDefaultQuizOption}
						addQuestion={AsyncMemoized.addQuestion}
						handleDeleteQuestion={Memoized.deleteQuizQuestion}
						Handlers={Memoized}
						language={language}
						questionErrors={questionErrors}
						setWorkingQuestion={setWorkingQuestion}
						setWorkingQuestionTitle={setWorkingQuestionTitle}
						setWorkingSurvey={setWorkingSurvey}
						surveyErrors={surveyErrors}
						workingQuestion={workingQuestion}
						workingSurvey={workingSurvey}
					/>
				);
			}
			default: {
				return <div></div>;
			}
		}
	}, [
		activeStep,
		AsyncMemoized,
		baseLanguage,
		createNewSurvey,
		featureFlags,
		handleDeleteQuestion,
		isLoadingSurveys,
		isQuiz,
		language,
		Memoized,
		questionErrors,
		RenderSurveyListItem,
		selectedSurveyIds,
		sensors,
		setWorkingQuestionTitle,
		setWorkingSurveyDescription,
		setWorkingSurveyTitle,
		surveys.length,
		surveyErrors,
		visibleSurveys.length,
		workingQuestion,
		workingSurvey,
	]);

	const finishedEditingQuestion = useCallback(() => {
		setWorkingSurvey((survey) => {
			if (!survey) { return null; }

			return {
				...survey,
				questions: survey.questions.map((question) => {
					if (
						workingQuestion &&
						question.survey_question === workingQuestion.survey_question
					) {
						return workingQuestion;
					}

					return question;
				}),
			};
		});
		goToQuestions();
	}, [goToQuestions, workingQuestion]);

	const saveSurvey = useCallback(async function() {
		if (!workingSurvey || !token) return;

		const cleanedSurvey = { ...workingSurvey };
		// if quiz, need to remove auto generated empty options and questions
		if (isQuiz) {
			cleanedSurvey.questions = cleanedSurvey.questions
				.filter((q) => q.options.length && q.title)
				.map((q) => ({
					...q,
					options: q.options.filter((o) => o.label),
				}));
		}

		const validationErrors = Memoized.validateSurvey(cleanedSurvey as CreateSurvey);
		if (validationErrors.length) return;

		setSaving(true);

		try {
			if ('survey' in cleanedSurvey) {
				const updatedSurvey = await UpdateSurvey(token, cleanedSurvey);
				dispatch(updateSurvey(updatedSurvey));
				dispatch(updatePageModule({
					...page_module,
					modules: page_module.modules?.map((pm: any) => pm.module_id === updatedSurvey.module_id ? updatedSurvey : pm),
					is_edited: true,
				}));
				setWorkingSurvey(updatedSurvey);
			} else {
				const newSurvey = await InsertSurvey(
					token,
					cleanedSurvey as CreateSurvey
				);
				dispatch(dispatchAddSurvey(newSurvey));
			}

			setSaving(false);
			setActiveStep(SurveySelectionSteps.select);
		} catch (e) {
			console.error(e);
		}
	}, [workingSurvey, token, isQuiz, Memoized, dispatch, page_module]);

	function handleTextChange(key: string) {
		return (text: string) => {
			const action = isEventLevel ? updateHomepageModule : updatePageModule;
			dispatch(
				action({
					...page_module,
					content: {
						...content,
						[key]: updateTranslateKey({
							translateString: page_module?.content?.[key],
							input: text,
							baseLanguage,
							language,
						}),
					},
					is_edited: true,
				})
			);
		};
	}

	function handleStyleOverrideChange(key: string) {
		return (overrides: TextOverrides) => {
			const action = isEventLevel ? updateHomepageModule : updatePageModule;
			const updatedOverrides = {
				...styling_overrides,
				content: {
					...styling_overrides?.content,
					[key]: overrides
				}
			};
			dispatch(
				action({
					...page_module,
					styling_overrides: updatedOverrides
				})
			);
		};
	}

	const ModalFooter = useMemo(() => {

		function onlySpaces(input: string): boolean {
			// check to see if inpout string is only spaces
			const output = input.replace(/\s/g, '');
			if (output.length) return false;
			return true;
		}

		function goToQuestionsDisabled() {
			if (activeStep === SurveySelectionSteps.create) {
				return !(
					workingSurvey?.name.changed &&
					workingSurvey?.description.changed &&
					!onlySpaces(workingSurvey?.name.base) &&
					!onlySpaces(workingSurvey?.description.base)
					// FUTURE DEV: when completion message added in
					// add this line to validation
					// workingSurvey?.completion_message.changed
				);
			} else {
				return !(
					workingSurvey?.name.base &&
					workingSurvey?.description.base &&
					!onlySpaces(workingSurvey?.name.base) &&
					!onlySpaces(workingSurvey?.description.base)
					// FUTURE DEV: when completion message added in
					// add this line to validation
					// workingSurvey?.completion_message.base
				);
			}
		}

		function createSurveyDisabled() {
			if (workingSurvey) {
				//is disabled if the working survey has no questions
				if (workingSurvey.questions.length < 1) {
					return true;
				}

				for (const question of workingSurvey.questions) {
					if (!question.title.base.length || onlySpaces(question.title.base)) {
						return true;
					}
				}
				if (surveyErrors.length > 0 || questionErrors.length > 0) {
					return true;
				}
			} else {
				return true;
			}
		}

		function updateQuestionDisabled() {
			if (
				workingQuestion?.question_type === SurveyQuestionType.checkbox ||
				workingQuestion?.question_type === SurveyQuestionType.radio
			) {
				if (!workingQuestion.options.length) {
					return true;
				}
				for (const option of workingQuestion.options) {
					if (!option.label.base.length || onlySpaces(option.label.base)) {
						return true;
					}
				}

			}

			return false;
		}

		const saveSurveyDisabled = isQuiz && !!questionErrors.length;

		const handleGoBack = (step: SurveySelectionSteps) => () => {
			setActiveStep(step);
		};

		switch (activeStep) {
			case SurveySelectionSteps.select: {
				return (
					<>
						<button
							onClick={closeModal}
							style={{ marginRight: 16 }}
						>
							Cancel
						</button>
						<button
							className='primary'
							onClick={doneSelectingSurveys}
						>
							Add
						</button>
					</>
				);
			}

			case SurveySelectionSteps.edit:
			case SurveySelectionSteps.create: {
				return (
					<>
						<button
							onClick={closeModal}
							style={{ marginRight: 16 }}
						>
							Close
						</button>
						<button
							className="surveys-primary-button primary"
							onClick={goToQuestions}
							disabled={goToQuestionsDisabled()}
						>
							Next
						</button>
					</>
				);
			}

			case SurveySelectionSteps.questionsList: {
				return workingSurvey && !('survey' in workingSurvey) ? (
					<>
						<button
							onClick={closeModal}
							style={{ marginRight: 16 }}
						>
							Cancel
						</button>
						<button
							onClick={saveSurvey}
							className="primary"
							disabled={createSurveyDisabled()}
						>
							Create Survey
						</button>
					</>
				) : (
					<>
						<button
							onClick={handleGoBack(SurveySelectionSteps.edit)}
							style={{ marginRight: 16 }}
						>
							Back
						</button>
						<button
							onClick={saveSurvey}
							className="primary"
							disabled={createSurveyDisabled()}
						>
							{saving ? <WaitingIndicator /> : 'Save'}
						</button>
					</>
				);
			}

			case SurveySelectionSteps.question: {
				return workingQuestion &&
					!('survey_question' in workingQuestion) ? (
					<>
						<button
							onClick={goToQuestions}
							style={{ marginRight: 16 }}
						>
							Cancel
						</button>
						<button
							onClick={finishedEditingQuestion}
							className="primary"
							disabled={updateQuestionDisabled()}
						>
							Add Question
						</button>
					</>
				) : (
					<>
						<button
							onClick={goToQuestions}
							style={{ marginRight: 16 }}
						>
							Back
						</button>
						<button
							onClick={finishedEditingQuestion}
							className="primary"
							disabled={updateQuestionDisabled()}
						>
							Save Question
						</button>
					</>
				);
			}

			case SurveySelectionSteps.quizModal: {
				return workingSurvey && (
					<>
						<button
							onClick={handleGoBack(SurveySelectionSteps.edit)}
							style={{ marginRight: 16 }}
						>
							Cancel
						</button>

						<button
							onClick={saveSurvey}
							className="primary surveys-primary-button"
							disabled={
								saveSurveyDisabled ||
								workingSurvey?.questions.length === 0 ||
								typeof workingSurvey?.passing_threshold !== "number" ||
								surveyErrors.length > 0}
						>

							{(workingSurvey as Survey)?.survey ? 'Save Quiz' : 'Create Quiz'}
						</button>
					</>
				);
			}

			default: {
				return (
					<>
						<button
							onClick={closeModal}
							style={{ marginRight: 16 }}
						>
							Cancel
						</button>
						<button
							onClick={doneSelectingSurveys}
							className="primary"
						>
							Done
						</button>
					</>
				);
			}
		}
	}, [
		activeStep,
		closeModal,
		doneSelectingSurveys,
		finishedEditingQuestion,
		goToQuestions,
		questionErrors.length,
		saveSurvey,
		saving,
		surveyErrors.length,
		workingQuestion,
		workingSurvey,
		isQuiz
	]);

	// Don't show if a) the module is off or b) there isn't a module and withMenu is on
	if (!page_module.is_on || (!page_module && workingSession?.layout?.withMenu)) {
		return null;
	}

	// What is shown in homepage (event level)
	if (isEventLevel) {
		const displayHeaderControls: InitialControlsDisplay = {
			editLayout: true,
			editColors: true,
			addContent: false,
			changeSectionPosition: true
		};

		return (
			<div
				ref={ref}
				className={classNames(
					'editor-wrapper home-surveys',
					backgroundColor,
					template
				)}
				style={backgroundImg}
			>
				<EventLevelControls
					display={displayHeaderControls}
					openQuizEditModal={Memoized.handleOpenSurveyModal}
					module={page_module}
					baseLanguage={baseLanguage}
					language={language}
				/>
				<div className="module-container">
					<EditableTextSlim
						className="surveys-custom-heading"
						tagType="p"
						value={t(`${namespace}:survey.${page_module.id}.heading`, t(`${namespace}:survey.heading`, custom_heading))}
						onChange={handleTextChange("custom_heading")}
						elementName={EContentColorOverrideNames.custom_heading}
						defaultColor={defaultCustomHeadingColor}
						colorOverride={styling_overrides?.content?.custom_heading?.color}
						onOverrideChange={handleStyleOverrideChange("custom_heading")}
					/>
					<EditableTextSlim
						className="survey-title"
						tagType="h2"
						elementClass="evt-heading-2"
						value={t(`${namespace}:survey.${page_module.id}.title`, t(`${namespace}:survey.title`, title))}
						onChange={handleTextChange("title")}
						elementName={EContentColorOverrideNames.title}
						defaultColor={ColorKeys.bodyTextColor}
						colorOverride={styling_overrides?.content?.title?.color}
						onOverrideChange={handleStyleOverrideChange("title")}
					/>
					{!page_module.modules?.length && (
						<LargeButton
							onClick={Memoized.handleOpenSurveyModal}
							title={'Add Survey'}
							style={{ width: '100%' }}
							nonUpload
						/>
					)}
					<div className="container">
						<div className="surveys-content surveys-content-home">
							{!!page_module.modules?.length
								&& page_module.modules.map((survey: Survey, index: number) => (
									<EditorSurvey
										key={`editor-survey-${index}`}
										content={content}
										survey={survey}
										lang={language}
										template={template}
									/>
								))}
						</div>
					</div>
				</div>
				<ModalComponent
					cancellable={true}
					closeable={false}
					open={modalOpen}
					onRequestClose={closeModal}
					title={modalTitle}
					subtitle={modalHeader}
					footer={ModalFooter}
				>
					<div className="surveys-modal">
						{ModalBody}
					</div>
				</ModalComponent>
			</div>
		);
	}

	// What is shown on the session page
	return (
		<div
			ref={ref}
			className={classNames(
				"editor-wrapper surveys",
				backgroundColor,
				template
			)}
			style={backgroundImg}
		>
			<Controls
				openEditModal={Memoized.handleOpenSurveyModal}
				page_module={page_module}
			/>
			<div className="container">
				<EditableTextSlim
					tagType="h5"
					className="ev-heading-5 surveys-custom-heading"
					value={t(`${namespace}:survey.${page_module.id}.heading`, t(`${namespace}:survey.heading`, custom_heading))}
					defaultColor={ColorKeys.bodyTextColor}
					onChange={handleTextChange('custom_heading')}
					onOverrideChange={handleStyleOverrideChange('custom_heading')}
					elementName={EContentColorOverrideNames.custom_heading}
					colorOverride={styling_overrides?.content?.custom_heading?.color}
				/>
				<div className="surveys-content">
					{page_module.modules?.length ?
						page_module.modules.map((survey: Survey, index: number) => (
							<EditorSurvey
								key={`editor-survey-${index}`}
								content={content}
								survey={survey}
								lang={language}
								template={template}
							/>
						))
						: (
							<LargeButton
								onClick={Memoized.handleOpenSurveyModal}
								title={'Add Survey'}
								nonUpload
							/>
						)}
				</div>
			</div>

			<ModalComponent
				cancellable={true}
				closeable={false}
				open={modalOpen}
				onRequestClose={closeModal}
				title={modalTitle}
				subtitle={modalHeader}
				footer={ModalFooter}
			>
				<div className="surveys-modal">
					{ModalBody}
				</div>
			</ModalComponent>
		</div>
	);
});

Surveys.displayName = 'Surveys';

export default Surveys;